import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { MutationResponse } from 'global';
import { useMutation, useQuery } from '@apollo/client';
import { PortalConfigurationType } from '@trnsact/trnsact-shared-types';
import { GET_LENDER_PROFILES_WITH_OCA_ACCESS, QUERY_PORTAL_CONFIGURATION } from 'queries/common';
import { Account, LenderProfilesWithOcaAccessResponse, LenderProfilesWithOcaAccessVariables } from 'types';

import { sendCreditApplicationApi } from './';
import { SendOcaLinkVariables } from './types';
import { SendApplicationFormValues, SendCreditApplicationRenderProps } from '../types';
import { formatFormValuesToSendOcaLinkPayload, getLenderProfilesWithOcaOptions } from '../lib';

interface Props {
  children: (sendCreditApplicationRenderProps: SendCreditApplicationRenderProps) => ReactNode;
}

export const ApiContainer = ({ children }: Props) => {
  const account = useSelector<any, Account>(state => state.account);

  const { data, loading: isLenderProfilesWithOcaAccessLoading } = useQuery<
    LenderProfilesWithOcaAccessResponse,
    LenderProfilesWithOcaAccessVariables
  >(GET_LENDER_PROFILES_WITH_OCA_ACCESS, {
    skip: !account?.id,
    variables: { accountId: account.id },
  });

  const { data: portalConfigurationsData, loading: isPortalConfigurationsLoading } = useQuery(
    QUERY_PORTAL_CONFIGURATION,
    {
      skip: !account?.vendorProfileId,
      fetchPolicy: 'no-cache',
      variables: { vendorProfileId: account?.vendorProfileId, types: [PortalConfigurationType.SendApplication] },
    }
  );

  const [sendOcaLink, { loading: isSendOcaLinkLoading }] = useMutation<MutationResponse<string>, SendOcaLinkVariables>(
    sendCreditApplicationApi.sendOcaLink
  );

  const handleSendOcaLink = async (formValues: SendApplicationFormValues) => {
    const input = formatFormValuesToSendOcaLinkPayload(formValues);

    const { data } = await sendOcaLink({ variables: { input } });

    if (!data) return null;

    return data?.result?.entity ?? null;
  };

  const lenderProfilesWithOcaOptions = getLenderProfilesWithOcaOptions(
    data?.lenderProfilesWithOcaAccess?.data ?? [],
    portalConfigurationsData?.portalConfigurations?.[0]?.jsonDefinition?.config
  );

  return (
    <>
      {children({
        handleSendOcaLink,
        isSendOcaLinkLoading,
        lenderProfilesWithOcaOptions,
        isDataLoading: isLenderProfilesWithOcaAccessLoading || isPortalConfigurationsLoading || !account?.id,
      })}
    </>
  );
};
